import { Decimal, money, roundMoney, type MoneyInput } from './money';

/**
 * Acumulador — Deriv's Accumulator contract (ACCU). Verified live on the public API (2026-10-13):
 *  - Every tick the price stays inside a corridor around the previous tick, the stake grows by the
 *    growth rate (compounded). The first tick outside it ends the contract and the stake is lost.
 *    The trader cashes out (sells) whenever they want, or a take profit closes it at a profit.
 *  - growth_rate accepts exactly 0.01–0.05; no duration (sending one is InvalidExpiry).
 *  - Stake 1.00–200.00 USD on every volatility index; payout capped at 3000; the contract also
 *    closes by itself after maximum_ticks (1HZ100V: 250 at 1%, 125 at 2%, 85 at 3%, 65 at 4%, 50 at 5%).
 *  - The corridor (barrier offset) is set by Deriv and changes over time, so it is never computed
 *    here: the live corridor and the run history come from Deriv's own ACCU proposal stream.
 */
export const ACCU_GROWTH_RATES = [0.01, 0.02, 0.03, 0.04, 0.05] as const;
export type AccuGrowthRate = (typeof ACCU_GROWTH_RATES)[number];
export const ACCU_DEFAULT_GROWTH: AccuGrowthRate = 0.03;
/**
 * The only growth the platform plays the Rush at (product decision 2026-10-14: one rate, never shown
 * to users). Every bet, robot, history and analysis uses it; the other Deriv rates stay accepted only
 * to read robots saved before, which run converted to it (rushTicksFrom).
 */
export const RUSH_GROWTH_RATE: AccuGrowthRate = 0.03;

/** Broker limits seen live; Deriv's own figures (proposal validation_params) are what count. */
export const ACCU_MIN_STAKE_USD = '1.00';
export const ACCU_MAX_STAKE_USD = '200.00';
/** Smallest take profit Deriv accepts. */
export const ACCU_MIN_TAKE_PROFIT = '0.01';
/** Finished runs kept per growth rate (history strip, bots). */
export const ACCU_RUNS_KEPT = 40;
/** Bots cash out after at most this many ticks inside the corridor. */
export const ACCU_MAX_CASHOUT_TICKS = 50;

export function isAccuGrowthRate(v: unknown): v is AccuGrowthRate {
  return typeof v === 'number' && (ACCU_GROWTH_RATES as readonly number[]).includes(v);
}

/**
 * Rush (Deriv ACCU) is switched off by product decision: the code stays ready (the contract exists at
 * Deriv on the 1s volatility indices) and turns on here.
 */
export const RUSH_ENABLED = false;

/** The Acumulador trades on the 1-second volatility indices (one tick per second, like the rounds). */
export function isAccumulatorSymbol(symbol: string): boolean {
  return RUSH_ENABLED && /^1HZ\d+V$/.test(symbol);
}

/** "3%" */
export function growthLabel(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

/** Value multiple after `ticks` ticks inside the corridor: (1 + g)^ticks. */
export function accuMultiplier(rate: number, ticks: number): number {
  return Math.pow(1 + rate, Math.max(0, ticks));
}

/**
 * Cash-out ticks at the Rush rate that keep the profit of `ticks` at `rate` without passing it (a robot
 * saved at another growth: 5% × 10 ticks = +62,9% → 3% × 16 ticks = +60,5%).
 */
export function rushTicksFrom(rate: number, ticks: number): number {
  if (rate === RUSH_GROWTH_RATE || !(rate > 0) || !(ticks > 0)) return ticks;
  return Math.min(ACCU_MAX_CASHOUT_TICKS, ticksWithin(RUSH_GROWTH_RATE, accuMultiplier(rate, ticks)));
}

/** Robot params with the Rush rate: growth and cash-out (top level or in `accu`) converted to it. */
export function normalizeRushParams<T>(params: T): T {
  if (!params || typeof params !== 'object') return params;
  const p = params as Record<string, unknown> & { accu?: { growthRate?: unknown; cashoutTicks?: unknown } };
  const fix = (g: unknown, t: unknown) => ({ growthRate: RUSH_GROWTH_RATE, cashoutTicks: typeof g === 'number' && typeof t === 'number' ? rushTicksFrom(g, t) : t });
  let out: Record<string, unknown> = p;
  if (typeof p.growthRate === 'number' || typeof p.cashoutTicks === 'number') out = { ...out, ...fix(p.growthRate ?? RUSH_GROWTH_RATE, p.cashoutTicks) };
  if (p.accu && typeof p.accu === 'object') out = { ...out, accu: { ...p.accu, ...fix(p.accu.growthRate ?? RUSH_GROWTH_RATE, p.accu.cashoutTicks) } };
  return out as T;
}

/** Fewest ticks inside the corridor for the value to reach `multiplier` (at least 1). */
export function ticksToReach(rate: number, multiplier: number): number {
  if (!(multiplier > 1) || !(rate > 0)) return 1;
  return Math.max(1, Math.ceil(Math.log(multiplier) / Math.log(1 + rate) - 1e-9));
}

/**
 * Most ticks whose value does not pass `multiplier` (at least 1): the value grows in steps of the
 * growth rate, so an automatic cash-out is snapped to a real step and closes exactly there, never above
 * what the user set (at 3%: "+100%" → 23 ticks, +97,4%).
 */
export function ticksWithin(rate: number, multiplier: number): number {
  if (!(multiplier > 1) || !(rate > 0)) return 1;
  return Math.max(1, Math.floor(Math.log(multiplier) / Math.log(1 + rate) + 1e-9));
}

/** "1,34x" */
export function formatMultiplier(x: number, digits = 2): string {
  const v = Number.isFinite(x) ? x : 1;
  return `${v.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits })}x`;
}

/**
 * Profit of a value multiple as a percentage, the Rush way: 1,47x → "+47%", 1,032x → "+3,2%",
 * 129,83x → "+12.883%". Rounded down: the screen never shows more than the value really is.
 */
export function formatProfitPct(multiple: number, sign = true): string {
  // A hair of tolerance: 1,47x is 46,9999…% in floating point and must still read +47%.
  const pct = (Number.isFinite(multiple) ? multiple - 1 : 0) * 100 + 1e-9;
  const whole = Math.abs(pct) >= 10;
  const v = whole ? Math.floor(pct) : Math.floor(pct * 10) / 10;
  const text = v.toLocaleString('pt-BR', { minimumFractionDigits: whole ? 0 : 1, maximumFractionDigits: whole ? 0 : 1 });
  return `${sign && v >= 0 ? '+' : ''}${text}%`;
}

/**
 * Take profit (a profit amount, as Deriv takes it) that closes the contract once its value reaches
 * `multiplier` × stake. Rounded down to cents so the contract closes on the tick that reaches it;
 * null when it would be under Deriv's minimum (0.01).
 */
export function takeProfitFor(stake: MoneyInput, multiplier: number, currency = 'USD'): string | null {
  if (!Number.isFinite(multiplier) || multiplier <= 1) return null;
  const tp = roundMoney(money(stake).times(new Decimal(multiplier).minus(1)), currency);
  return tp.lt(ACCU_MIN_TAKE_PROFIT) ? null : tp.toFixed(2);
}

/** Take profit that closes the contract after `ticks` ticks inside the corridor (bots). */
export function takeProfitForTicks(stake: MoneyInput, rate: number, ticks: number, currency = 'USD'): string | null {
  const growth = new Decimal(1 + rate).pow(Math.max(1, Math.floor(ticks))).minus(1);
  const tp = roundMoney(money(stake).times(growth), currency);
  return tp.lt(ACCU_MIN_TAKE_PROFIT) ? null : tp.toFixed(2);
}

// ---------------------------------------------------------------- market

/** The Acumulador market at one growth rate, from Deriv's public ACCU proposal stream. */
export interface AccuMarketState {
  symbol: string;
  growthRate: AccuGrowthRate;
  /** Tick this state is for (Deriv spot_time, epoch seconds). */
  epoch: number;
  spot: number;
  /** Corridor the NEXT tick must stay inside (Deriv's barriers around this tick). */
  high: number;
  low: number;
  /** Ticks the price has stayed inside since the last break (Deriv's current run). */
  run: number;
  /** Finished runs, newest first: how many ticks each one lasted before its break. */
  runs: number[];
  /** This tick broke the corridor (a new run started on it). */
  broke: boolean;
  maxTicks: number | null;
  maxPayout: string | null;
  minStake: string | null;
  maxStake: string | null;
  /** The Decolagem round at this growth rate (see advanceAccuRound). */
  round?: AccuRound;
  /** Finished rounds, newest first: round id and the ticks it flew. */
  flights?: AccuFlight[];
}

/** A finished round. `takeoff`/`crashedAt` (epochs) are sent by newer leaders; older entries lack them. */
export interface AccuFlight {
  id: number;
  ticks: number;
  takeoff?: number;
  crashedAt?: number;
}

/** Longest gap between two updates for a break to be placed on the newer tick (1s and 2s indices). */
const ACCU_BREAK_GAP_S = 5;

/**
 * Advance the run history with one update of Deriv's `ticks_stayed_in` (oldest first, the last
 * value is the run in progress). The first answer of a stream carries the whole history; the
 * following ones only the current run. A run that did not grow means the corridor broke since the
 * previous update: the previous run is finished (two breaks in a row make a run of 0). The break is
 * placed on this tick only when the previous update is recent; after a gap (reconnect) it is kept
 * in the history but `broke` stays false, so nothing acts on a break that happened earlier.
 */
export function advanceAccuRuns(
  prev: Pick<AccuMarketState, 'epoch' | 'run' | 'runs'> | null,
  epoch: number,
  stayedIn: readonly number[],
): { run: number; runs: number[]; broke: boolean } | null {
  const valid = stayedIn.filter((n) => Number.isInteger(n) && n >= 0);
  if (!valid.length || !Number.isFinite(epoch)) return null;
  const run = valid[valid.length - 1]!;
  if (prev && epoch <= prev.epoch) return { run: prev.run, runs: prev.runs, broke: false };
  const ended = prev !== null && run <= prev.run;
  const broke = ended && epoch - prev!.epoch <= ACCU_BREAK_GAP_S;
  let runs: number[];
  if (valid.length > 1) runs = valid.slice(0, -1).reverse();
  else if (ended) runs = [prev!.run, ...prev!.runs];
  else runs = prev?.runs ?? [];
  return { run, runs: runs.slice(0, ACCU_RUNS_KEPT), broke };
}

// ---------------------------------------------------------------- rounds (Decolagem)

/**
 * Decolagem rounds, like a crash game, run by the market-data leader for every symbol and growth
 * rate from Deriv's own corridor breaks, so every screen sees the same round:
 *   BETTING  ACCU_BET_SECONDS: bets are taken; they are all bought in second `takeoff - 1`, so
 *            their entry spot is tick `takeoff` (Deriv: first tick after the purchase second).
 *   FLYING   from `takeoff`: ticks inside the corridor since then; the value is (1+g)^ticks.
 *   CRASHED  the first break after `takeoff` (or Deriv's maximum ticks) ends it; shown for
 *            ACCU_CRASH_SECONDS, then the next round opens for bets.
 * A bet's money is its own Deriv contract: the round only times when it starts.
 */
export const ACCU_BET_SECONDS = 6;
export const ACCU_CRASH_SECONDS = 3;
/** Bets are sent this many ms into second `takeoff - 1`. */
export const ACCU_SEND_OFFSET_MS = 30;
/** The server takes bets until this long before the send moment; screens close a little earlier. */
export const ACCU_BET_CLOSE_MS = 350;
export const ACCU_BET_CLOSE_UI_MS = 900;

export interface AccuRound {
  id: number;
  /** Epoch (s) of the takeoff tick: the entry tick of every bet of this round. */
  takeoff: number;
  /** Epoch of the tick that broke the corridor (or Deriv closed the contracts), null while betting/flying. */
  crashedAt: number | null;
  /** Ticks flown inside the corridor (at the crash: the final count). */
  ticks: number;
  /** 'landed' = Deriv's maximum ticks reached (the bets were paid at that value). */
  end: 'break' | 'landed' | null;
}

export type AccuRoundPhase = 'BETTING' | 'FLYING' | 'CRASHED';

export function accuRoundPhase(r: AccuRound, epoch: number): AccuRoundPhase {
  if (r.crashedAt !== null) return 'CRASHED';
  return epoch < r.takeoff ? 'BETTING' : 'FLYING';
}

/** Server moment (ms) the bets of a round are bought. */
export function accuSendAtMs(takeoff: number): number {
  return (takeoff - 1) * 1000 + ACCU_SEND_OFFSET_MS;
}

/**
 * Advance a round with the market update of tick `epoch` (`broke`: this tick left the corridor).
 * Pure: the leader runs it on every update; tests run it on recorded sequences.
 */
export function advanceAccuRound(prev: AccuRound | null, epoch: number, broke: boolean, maxTicks: number | null): AccuRound {
  // A round lasts at least 10s (6s bets, 1 tick, 3s result): ids started from epoch/9 never go back
  // after a leader restart, so counts of rounds (robot pauses, paper entries) stay meaningful.
  if (!prev) return { id: Math.floor(epoch / 9), takeoff: epoch + ACCU_BET_SECONDS, crashedAt: null, ticks: 0, end: null };
  if (prev.crashedAt !== null) {
    if (epoch >= prev.crashedAt + ACCU_CRASH_SECONDS) return { id: prev.id + 1, takeoff: epoch + ACCU_BET_SECONDS, crashedAt: null, ticks: 0, end: null };
    return prev;
  }
  if (epoch <= prev.takeoff) return prev;
  // The bets' first comparison is tick takeoff+1 against the entry tick: a break from then on ends the round.
  if (broke) return { ...prev, crashedAt: epoch, ticks: epoch - 1 - prev.takeoff, end: 'break' };
  const ticks = epoch - prev.takeoff;
  if (maxTicks !== null && ticks >= maxTicks) return { ...prev, crashedAt: epoch, ticks: maxTicks, end: 'landed' };
  return { ...prev, ticks };
}

/** A finished round as stored and sent: [id, takeoff, crashedAt, ticks, landed (0/1)]. */
export type PackedAccuRound = [number, number, number, number, 0 | 1];

/** A finished Rush round from the history. */
export interface RushRound {
  id: number;
  takeoff: number;
  crashedAt: number;
  ticks: number;
  /** Deriv's maximum ticks closed it (the bets were paid). */
  landed: boolean;
}

export function packAccuRound(r: AccuRound): PackedAccuRound {
  return [r.id, r.takeoff, r.crashedAt ?? r.takeoff + r.ticks, r.ticks, r.end === 'landed' ? 1 : 0];
}

export function unpackAccuRound(p: readonly number[]): RushRound | null {
  const [id, takeoff, crashedAt, ticks, landed] = p;
  if (![id, takeoff, crashedAt, ticks].every((v) => Number.isSafeInteger(v)) || ticks! < 0) return null;
  return { id: id!, takeoff: takeoff!, crashedAt: crashedAt!, ticks: ticks!, landed: landed === 1 };
}

/** One round on the time axis: profit climbs from `takeoff` for `ticks` ticks and drops at `takeoff + ticks`. */
export interface AccuTimelineRound {
  id: number;
  takeoff: number;
  ticks: number;
  /** Still flying: `ticks` are the ticks confirmed so far. */
  live: boolean;
}

/**
 * The rounds on screen, oldest first, placed in time from the server's data only (the current round and
 * the finished ones), so every viewer — whenever they opened the page — draws the same chart. Finished
 * rounds without times (from older leaders) are placed back from the round after them: the next round
 * opens ACCU_CRASH_SECONDS after the break and takes off ACCU_BET_SECONDS later, the break being the
 * tick after the last one flown. Only consecutive ids are chained; `sinceEpoch` bounds how far back.
 */
export function accuTimeline(round: AccuRound | null, flights: readonly AccuFlight[], marketEpoch: number | null, sinceEpoch: number): AccuTimelineRound[] {
  const out: AccuTimelineRound[] = [];
  let nextId: number | null = null;
  let nextTakeoff: number | null = null;
  if (round) {
    const flying = round.crashedAt === null;
    if (!flying || (marketEpoch !== null && marketEpoch >= round.takeoff)) {
      const ticks = flying ? Math.max(0, Math.min(round.ticks, (marketEpoch ?? round.takeoff) - round.takeoff)) : round.ticks;
      out.push({ id: round.id, takeoff: round.takeoff, ticks, live: flying });
    }
    nextId = round.id;
    nextTakeoff = round.takeoff;
  }
  for (const f of flights) {
    if (round && f.id >= round.id) continue;
    let takeoff: number | null = Number.isFinite(f.takeoff) ? f.takeoff! : null;
    if (takeoff === null && nextId !== null && nextTakeoff !== null && f.id === nextId - 1) {
      takeoff = nextTakeoff - ACCU_BET_SECONDS - ACCU_CRASH_SECONDS - 1 - f.ticks;
    }
    if (takeoff === null) break;
    out.push({ id: f.id, takeoff, ticks: f.ticks, live: false });
    nextId = f.id;
    nextTakeoff = takeoff;
    if (takeoff + f.ticks < sinceEpoch) break;
  }
  return out.reverse();
}

// ---------------------------------------------------------------- live contract

/** An open Acumulador contract, streamed to its owner every tick. */
export interface AccuLiveDTO {
  contractId: string;
  accountId: string;
  symbol: string;
  growthRate: number;
  currency: string;
  stake: string;
  /** What selling now pays (Deriv bid_price). */
  bid: string;
  profit: string;
  /** Ticks inside the corridor so far. */
  ticks: number;
  epoch: number | null;
  spot: number | null;
  /** Corridor for the next tick. */
  high: number | null;
  low: number | null;
  validToSell: boolean;
  takeProfit: string | null;
}
